/**
 * Toast API Hook
 * Provides access to Toast POS data through the proxy API
 */
import { useState, useCallback } from 'react';
import { post, get } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';

const useToastAPI = () => {
  const { getAuthToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Call the Toast API proxy
   * @param {string} api - API name (labor, config, menus, orders, restaurants, stock, cash_management)
   * @param {string} method - Method name from the API
   * @param {object} params - Optional parameters for the method
   */
  const callToastAPI = useCallback(async (api, method, params = {}) => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAuthToken();
      const response = await post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/toast',
        options: {
          headers: { Authorization: token },
          body: { api, method, params }
        }
      }).response;

      const data = await response.body.json();

      if (!data.success) {
        throw new Error(data.error || 'Toast API request failed');
      }

      setLoading(false);
      return data.data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch Toast data';
      setError(errorMessage);
      setLoading(false);
      throw err;
    }
  }, [getAuthToken]);

  // ==================== LABOR API ====================

  const getEmployees = useCallback(async (lastModified = null) => {
    return callToastAPI('labor', 'get_employees', lastModified ? { last_modified: lastModified } : {});
  }, [callToastAPI]);

  const getEmployee = useCallback(async (guid) => {
    return callToastAPI('labor', 'get_employee', { guid });
  }, [callToastAPI]);

  const getJobs = useCallback(async (lastModified = null) => {
    return callToastAPI('labor', 'get_jobs', lastModified ? { last_modified: lastModified } : {});
  }, [callToastAPI]);

  const getJob = useCallback(async (guid) => {
    return callToastAPI('labor', 'get_job', { guid });
  }, [callToastAPI]);

  const getShifts = useCallback(async (startDate = null, endDate = null, employeeGuid = null) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (employeeGuid) params.employee_guid = employeeGuid;
    return callToastAPI('labor', 'get_shifts', params);
  }, [callToastAPI]);

  const getTimeEntries = useCallback(async (startDate = null, endDate = null, businessDate = null, employeeGuid = null) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (businessDate) params.business_date = businessDate;
    if (employeeGuid) params.employee_guid = employeeGuid;
    return callToastAPI('labor', 'get_time_entries', params);
  }, [callToastAPI]);

  const getBreaks = useCallback(async (startDate = null, endDate = null, businessDate = null) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (businessDate) params.business_date = businessDate;
    return callToastAPI('labor', 'get_breaks', params);
  }, [callToastAPI]);

  // ==================== CONFIG API ====================

  const getBreakTypes = useCallback(async () => {
    return callToastAPI('config', 'get_break_types');
  }, [callToastAPI]);

  const getDiningOptions = useCallback(async () => {
    return callToastAPI('config', 'get_dining_options');
  }, [callToastAPI]);

  const getServiceAreas = useCallback(async () => {
    return callToastAPI('config', 'get_service_areas');
  }, [callToastAPI]);

  const getRestaurantServices = useCallback(async () => {
    return callToastAPI('config', 'get_restaurant_services');
  }, [callToastAPI]);

  const getTables = useCallback(async () => {
    return callToastAPI('config', 'get_tables');
  }, [callToastAPI]);

  const getRevenueCenters = useCallback(async () => {
    return callToastAPI('config', 'get_revenue_centers');
  }, [callToastAPI]);

  // ==================== RESTAURANTS API ====================

  const getRestaurant = useCallback(async (restaurantGuid = null) => {
    return callToastAPI('restaurants', 'get_restaurant', restaurantGuid ? { restaurant_guid: restaurantGuid } : {});
  }, [callToastAPI]);

  const getRestaurantLocation = useCallback(async () => {
    return callToastAPI('restaurants', 'get_restaurant_location');
  }, [callToastAPI]);

  const getRestaurantSchedule = useCallback(async () => {
    return callToastAPI('restaurants', 'get_restaurant_schedule');
  }, [callToastAPI]);

  // ==================== ORDERS API ====================

  const getOrders = useCallback(async (businessDate = null, startDate = null, endDate = null) => {
    const params = {};
    if (businessDate) params.business_date = businessDate;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return callToastAPI('orders', 'get_orders', params);
  }, [callToastAPI]);

  const getOrdersBulk = useCallback(async (businessDate = null, startDate = null, endDate = null) => {
    const params = {};
    if (businessDate) params.business_date = businessDate;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return callToastAPI('orders', 'get_orders_bulk', params);
  }, [callToastAPI]);

  // ==================== STOCK API ====================

  const getInventory = useCallback(async (status = null) => {
    return callToastAPI('stock', 'get_inventory', status ? { status } : {});
  }, [callToastAPI]);

  const getOutOfStockItems = useCallback(async () => {
    return callToastAPI('stock', 'get_out_of_stock_items');
  }, [callToastAPI]);

  // ==================== CASH MANAGEMENT API ====================

  const getCashEntries = useCallback(async (businessDate = null) => {
    return callToastAPI('cash_management', 'get_cash_entries', businessDate ? { business_date: businessDate } : {});
  }, [callToastAPI]);

  const getDeposits = useCallback(async (businessDate = null) => {
    return callToastAPI('cash_management', 'get_deposits', businessDate ? { business_date: businessDate } : {});
  }, [callToastAPI]);

  // ==================== MENUS API ====================

  const getMenus = useCallback(async () => {
    return callToastAPI('menus', 'get_menus');
  }, [callToastAPI]);

  const getMenuGroups = useCallback(async () => {
    return callToastAPI('menus', 'get_menu_groups');
  }, [callToastAPI]);

  const getMenuItems = useCallback(async () => {
    return callToastAPI('menus', 'get_menu_items');
  }, [callToastAPI]);

  const getFullMenu = useCallback(async () => {
    return callToastAPI('menus', 'get_full_menu');
  }, [callToastAPI]);

  return {
    // State
    loading,
    error,

    // Generic call
    callToastAPI,

    // Labor
    getEmployees,
    getEmployee,
    getJobs,
    getJob,
    getShifts,
    getTimeEntries,
    getBreaks,

    // Config
    getBreakTypes,
    getDiningOptions,
    getServiceAreas,
    getRestaurantServices,
    getTables,
    getRevenueCenters,

    // Restaurants
    getRestaurant,
    getRestaurantLocation,
    getRestaurantSchedule,

    // Orders
    getOrders,
    getOrdersBulk,

    // Stock
    getInventory,
    getOutOfStockItems,

    // Cash Management
    getCashEntries,
    getDeposits,

    // Menus
    getMenus,
    getMenuGroups,
    getMenuItems,
    getFullMenu,
  };
};

export default useToastAPI;
